import React, { useEffect, useState } from 'react';
import { useMarket } from '../contexts/MarketContext';
import { useAddToCart } from '../hooks/useAddToCart';

/**
 * Mobile-only sticky purchase bar. Appears after the hero and hides
 * again while the final CTA is on screen so the CTA is never duplicated.
 *
 * The bar is always in the DOM and only transformed off-screen
 * (translateY + visibility), so showing it never triggers layout.
 */
export function StickyMobileCTA({
  hideAtId
}: {
  hideAtId: string;
}) {
  const {
    market,
    cartHref
  } = useMarket();
  const onAddToCart = useAddToCart();
  const [visible, setVisible] = useState(false);
  const [suppressed, setSuppressed] = useState(false);
  useEffect(() => {
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => {
        setVisible(window.scrollY > 650);
        queued = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const target = document.getElementById(hideAtId);
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setSuppressed(entry.isIntersecting), {
      threshold: 0.15
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hideAtId]);
  const show = visible && !suppressed;
  return <div className="fixed inset-x-0 bottom-0 z-50 border-t border-navy/10 bg-white/95 px-4 py-3 backdrop-blur md:hidden" style={{
    transform: show ? 'translate3d(0, 0, 0)' : 'translate3d(0, 110%, 0)',
    visibility: show ? 'visible' : 'hidden',
    transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)'
  }} aria-hidden={!show} data-id="element-293" data-mp="24awntywa2tad">
      <div className="flex items-center gap-3" data-id="element-294" data-mp="3tn8kzuqhwez1">
        <div className="shrink-0" data-id="element-295" data-mp="3pp12auch13l7">
          <p className="font-display text-lg font-extrabold leading-none text-navy" data-id="element-296" data-mp="3plvctdzxjzoy">
            {market.price}
          </p>
          <p className="mt-0.5 font-display text-[10px] font-extrabold uppercase tracking-wide text-offer" data-id="element-297" data-mp="1rs10fu04dabg">
            50% OFF
          </p>
        </div>
        <a href={cartHref} onClick={onAddToCart} tabIndex={show ? 0 : -1} className="flex-1 rounded-xl bg-brandgreen px-4 py-3 text-center font-display text-xs font-extrabold uppercase tracking-wide text-white shadow-cta transition-transform duration-200 active:scale-[0.98] motion-reduce:transition-none" data-id="element-298" data-mp="1e27b7akpco17">
          Quiero las 365 ideas
        </a>
      </div>
    </div>;
}